import { alpha, createTheme, type Theme } from "@mui/material/styles";
import dmSansWoff2 from "../fonts/dm-sans-variablefont_opsz_wght-webfont.woff2";
import golosWoff2 from "../fonts/golostext-variablefont_wght-webfont.woff2";
import interWoff2 from "../fonts/inter-variablefont_opsz_wght-webfont.woff2";
import manropeWoff2 from "../fonts/manrope-variablefont_wght-webfont.woff2";
import montserratWoff2 from "../fonts/montserrat-variablefont_wght-webfont.woff2";
import {
	buildColorPalette,
	buildPrimaryPalette,
	DEFAULT_NEUTRAL_COLOR,
	DEFAULT_PRIMARY_COLOR,
	DEFAULT_SECONDARY_COLOR,
	DEFAULT_TERTIARY_COLOR,
} from "./palette";
import { ALPHA, DEFAULT_BODY_FONT, DEFAULT_HEADLINE_FONT, DEFAULT_LABEL_FONT, FONT_STACK_BY_ID, type FontId, INK, NEUTRAL, STATUS, SURFACE } from "./tokens";

/** Per-org/per-user theme overrides. Any unset axis falls back to its flagged default. */
export interface ThemeOverrides {
	primary?: string;
	secondary?: string;
	tertiary?: string;
	neutral?: string;
	headlineFont?: FontId;
	bodyFont?: FontId;
	labelFont?: FontId;
}

export const buildAppTheme = (overrides: ThemeOverrides = {}): Theme => {
	const {
		primary: primaryHex = DEFAULT_PRIMARY_COLOR,
		secondary: secondaryHex = DEFAULT_SECONDARY_COLOR,
		tertiary: tertiaryHex = DEFAULT_TERTIARY_COLOR,
		neutral: neutralHex = DEFAULT_NEUTRAL_COLOR,
		headlineFont = DEFAULT_HEADLINE_FONT,
		bodyFont = DEFAULT_BODY_FONT,
		labelFont = DEFAULT_LABEL_FONT,
	} = overrides;

	const primary = buildPrimaryPalette(primaryHex);
	const secondary = buildColorPalette(secondaryHex);
	const tertiary = buildColorPalette(tertiaryHex);
	const neutral = buildColorPalette(neutralHex);

	// Typography roles → font stacks. headline drives h1–h6; body drives the
	// base family (cascades to body/subtitle); label drives buttons + captions
	// + form labels.
	const headlineFamily = FONT_STACK_BY_ID[headlineFont];
	const bodyFamily = FONT_STACK_BY_ID[bodyFont];
	const labelFamily = FONT_STACK_BY_ID[labelFont];

	// Soften the chosen primary for the app background so it tints subtly
	// without making the page feel saturated. Slightly heavier tint than the
	// hover overlay so the page reads "themed" rather than "gray".
	const tintedBackground = alpha(primary.main, 0.07);

	return createTheme({
		breakpoints: {
			values: {
				xs: 0,
				mediumPhone: 400,
				sm: 600,
				md: 900,
				lg: 1200,
				xl: 1536,
				xxl: 1920,
			},
		},
		palette: {
			primary: {
				main: primary.main,
				light: primary.light,
				dark: primary.dark,
				contrastText: primary.contrastText,
				...primary.shades,
			},
			secondary: {
				main: secondary.main,
				light: secondary.light,
				dark: secondary.dark,
				contrastText: secondary.contrastText,
				...secondary.shades,
			},
			tertiary: {
				main: tertiary.main,
				light: tertiary.light,
				dark: tertiary.dark,
				contrastText: tertiary.contrastText,
				...tertiary.shades,
			},
			neutral: {
				main: neutral.main,
				light: neutral.light,
				dark: neutral.dark,
				contrastText: neutral.contrastText,
				...neutral.shades,
			},
			error: STATUS.ERROR,
			warning: STATUS.WARNING,
			success: STATUS.SUCCESS,
			info: STATUS.INFO,
			background: {
				default: tintedBackground,
				paper: SURFACE,
			},
			text: {
				primary: INK.PRIMARY,
				secondary: INK.SECONDARY,
				disabled: INK.DISABLED,
			},
			// Derives from the neutral palette so the neutral control visibly
			// retints borders/dividers. Defaults to today's NEUTRAL[300].
			divider: neutral.main,
		},
		typography: {
			fontFamily: bodyFamily,
			h1: { fontFamily: headlineFamily, fontWeight: 600, fontSize: "3rem", lineHeight: 1.2 },
			h2: { fontFamily: headlineFamily, fontWeight: 600, fontSize: "2.5rem", lineHeight: 1.3 },
			h3: { fontFamily: headlineFamily, fontWeight: 600, fontSize: "2rem", lineHeight: 1.4 },
			h4: { fontFamily: headlineFamily, fontWeight: 600, fontSize: "1.75rem", lineHeight: 1.4 },
			h5: { fontFamily: headlineFamily, fontWeight: 600, fontSize: "1.5rem", lineHeight: 1.5 },
			h6: { fontFamily: headlineFamily, fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.6 },
			body1: { fontSize: "1rem", lineHeight: 1.6 },
			body2: { fontSize: "0.875rem", lineHeight: 1.6 },
			button: { fontFamily: labelFamily, fontWeight: 400, textTransform: "none" },
			caption: { fontFamily: labelFamily },
			overline: { fontFamily: labelFamily },
		},
		shape: { borderRadius: 8 },
		components: {
			MuiTableCell: {
				styleOverrides: {
					root: { height: "10px", padding: "0px 0px" },
				},
			},
			MuiButton: {
				styleOverrides: {
					root: ({ ownerState }) => ({
						borderRadius: "8px",
						padding: "10px 20px",
						...(ownerState.variant === "contained" &&
							ownerState.color === "primary" && {
								color: primary.contrastText,
								"&.Mui-disabled": {
									color: alpha(primary.contrastText, 0.6),
								},
								"&.Mui-focusVisible": {
									outline: `2px solid ${primary.main}`,
									outlineOffset: "1px",
								},
							}),
					}),
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						boxShadow: `0 4px 6px ${alpha(primary.main, ALPHA.SHADOW_SOFT)}`,
					},
				},
			},
			MuiAppBar: {
				styleOverrides: {
					// The app's single AppBar (`headerStyles.appBar` in
					// `components/header/utils.ts`) draws a primary-gradient
					// background. We mirror that intent here so any AppBar
					// added without the gradient sx still pairs a dark primary
					// surface with light text — never near-white on near-white.
					root: {
						backgroundColor: primary.dark,
						color: primary.contrastText,
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: `
					@font-face {
						font-family: 'Manrope';
						font-style: normal;
						font-weight: 200 800;
						src: url(${manropeWoff2}) format('woff2');
						font-display: swap;
					}
					@font-face {
						font-family: 'Inter';
						font-style: normal;
						font-weight: 100 900;
						src: url(${interWoff2}) format('woff2');
						font-display: swap;
					}
					@font-face {
						font-family: 'DM Sans';
						font-style: normal;
						font-weight: 400 700;
						src: url(${dmSansWoff2}) format('woff2');
						font-display: swap;
					}
					@font-face {
						font-family: 'Montserrat';
						font-style: normal;
						font-weight: 100 900;
						src: url(${montserratWoff2}) format('woff2');
						font-display: swap;
					}
					@font-face {
						font-family: 'Golos Text';
						font-style: normal;
						font-weight: 400 900;
						src: url(${golosWoff2}) format('woff2');
						font-display: swap;
					}
					body {
						background-color: ${tintedBackground};
					}
					a {
						color: ${primary.dark};
						text-decoration: none;
					}
					a:hover {
						text-decoration: underline;
					}
					/* Global keyboard-focus outline. Applies to any focusable element — including
					   Box-as-button (role="button" tabIndex={0}) — so non-MUI interactive surfaces
					   inherit the same themed focus ring as MUI ButtonBase descendants. */
					:focus-visible {
						outline: 2px solid ${primary.main};
						outline-offset: 1px;
					}
				`,
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						color: primary.main,
						"&.Mui-checked": { color: primary.main },
					},
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						border: `1px solid ${INK.SECONDARY}`,
						color: INK.SECONDARY,
						backgroundColor: SURFACE,
						"&.Mui-selected": {
							backgroundColor: INK.SECONDARY,
							color: SURFACE,
							"&:hover": { backgroundColor: INK.SECONDARY },
						},
						"&.Mui-disabled": {
							border: `1px solid ${NEUTRAL[300]}`,
							color: NEUTRAL[300],
						},
						transition: "background-color 0.3s, color 0.3s",
					},
				},
			},
			MuiToggleButtonGroup: {
				styleOverrides: {
					root: {
						backgroundColor: "transparent",
						"& .MuiToggleButtonGroup-grouped": {
							"&:not(:first-of-type)": {
								borderLeft: `1px solid ${INK.SECONDARY}`,
							},
						},
					},
				},
			},
			MuiInput: { styleOverrides: { root: { color: INK.SECONDARY } } },
			MuiSelect: { styleOverrides: { root: { color: INK.SECONDARY } } },
			MuiOutlinedInput: { styleOverrides: { root: { color: INK.SECONDARY } } },
			MuiInputBase: {
				styleOverrides: {
					input: {
						"&.Mui-disabled": {
							WebkitTextFillColor: INK.SECONDARY,
							opacity: 0.6,
						},
					},
				},
			},
			// Form labels follow the label-font role (so do InputLabels, which
			// extend FormLabel) alongside the button/caption/overline variants.
			MuiFormLabel: { styleOverrides: { root: { fontFamily: labelFamily } } },
			MuiInputLabel: { styleOverrides: { root: { fontFamily: labelFamily } } },
		},
	});
};
